import Constants from "@/constants";
import {
  effectiveOptType,
  getEnrollmentParameter,
} from "@/utils/enrollmentFlow";

export const GENERAL_ENROLLMENT_EVENTS = {
  FORM_OPEN: "enrollment_form_open",
  STEP_VIEW: "enrollment_step_view",
  QUESTIONS_COMPLETE: "enrollment_questions_complete",
  GET_PLAN: "enrollment_get_plan",
  PLAN_SELECTED: "enrollment_plan_selected",
  CHECKOUT_STARTED: "enrollment_checkout_started",
  MEMBER_VALIDATED: "enrollment_member_validated",
  PAYMENT_STEP_COMPLETE: "enrollment_payment_step_complete",
  SUBMIT: "enrollment_submit",
  SUCCESS: "enrollment_success",
  FAILED: "enrollment_failed",
};

export const isGeneralEnrollmentTrackingEnabled = () => {
  if (typeof window.__generalEnrollmentTrackingEnabled === "boolean") {
    return window.__generalEnrollmentTrackingEnabled;
  }
  return Constants.isTestEnvironment() && Constants.isGeneralEnrollmentUrl();
};

export const buildGeneralEnrollmentContext = (state = {}, extra = {}) => ({
  opt_type: state.opt_type || "general",
  enrollment_parameter:
    state.enrollment_parameter || getEnrollmentParameter(state) || "",
  effective_opt_type: effectiveOptType(state),
  who_for: state.whoFor || "",
  has_cdl: state.hasCDL || "",
  plan_interest: state.planInterest || "",
  payment_method: state.payment_method || "",
  enroll_option: state.enroll_option || "",
  page_url: window.location.href,
  referrer: document.referrer || "",
  ...extra,
});

const parseAnalyticsMoney = (value) => {
  if (value === undefined || value === null || value === "") return 0;
  const cleaned = String(value).replace(/[^0-9.+-]/g, "");
  const num = parseFloat(cleaned);
  return Number.isFinite(num) ? num : 0;
};

const parseMultiValueField = (raw) => {
  if (raw === undefined || raw === null || raw === "") return [];
  const str = String(raw).trim();
  if (str.includes(",")) {
    return str.split(",").map((part) => part.trim()).filter(Boolean);
  }
  if (str.includes(" + ")) {
    return str.split(" + ").map((part) => part.trim()).filter(Boolean);
  }
  return [str];
};

const splitPlanDescriptions = (description) => {
  if (!description) return [];
  const str = String(description).trim();
  if (str.includes(" & ")) return str.split(" & ").map((part) => part.trim()).filter(Boolean);
  if (str.includes(" and ")) return str.split(" and ").map((part) => part.trim()).filter(Boolean);
  return [str];
};

export const resolveBillingCycle = (state = {}) => {
  const studentPeriod = String(state.student_billing_period || "").toLowerCase();
  if (studentPeriod === "annual") return "yearly";
  if (studentPeriod === "monthly") return "monthly";

  const frequency = Number(state.payment_frequency);
  if (frequency === 1) return "yearly";
  if (frequency === 12) return "monthly";
  return "monthly";
};

const inferItemCategory = (state = {}, selectedPlan = {}) => {
  const interest = String(state.planInterest || "").toLowerCase();
  const description = String(selectedPlan.description || "").toLowerCase();
  const planCode = String(selectedPlan.plan_code || "").toUpperCase();

  if (interest.includes("cdl") || description.includes("cdl") || planCode.includes("CDL")) {
    return "CDL Legal";
  }
  if (
    interest.includes("identity") ||
    description.includes("identity defender") ||
    planCode.includes("ID")
  ) {
    return "Identity Protection";
  }
  if (interest.includes("student") || description.includes("student")) {
    return "Student Legal";
  }
  return "Legal Services";
};

export const inferProductTier = (selectedPlan = {}, state = {}) => {
  const description = String(selectedPlan.description || "").toLowerCase();
  const planCode = String(selectedPlan.plan_code || "").toLowerCase();

  if (description.includes("platinum") || planCode.includes("plat")) return "platinum";
  if (description.includes("gold") || planCode.includes("gold")) return "gold";
  if (description.includes("silver") || planCode.includes("silver")) return "silver";
  if (String(state.planInterest || "").toLowerCase().includes("gold")) return "gold";
  return "";
};

const resolveTransactionId = (response = {}, state = {}, memberDetails = {}) => {
  const data = response?.data || {};
  const candidates = [
    data.transaction_id,
    data.transactionId,
    data.subscriptionId,
    data.subscription_id,
    data.member_id,
    data?.data?.transaction_id,
    data?.data?.transactionId,
    data?.data?.subscriptionId,
    data?.data?.member_id,
    data?.event?.processPaymentResult?.transactionResponse?.transId,
    data?.event?.processPaymentResult?.subscriptionId,
    data?.event?.createMemberResult?.member_id,
    state?.member?.member_id,
    memberDetails?.member_id,
  ];

  const found = candidates.find(
    (value) => value !== undefined && value !== null && String(value).trim() !== ""
  );
  return found ? String(found) : "";
};

const resolveCustomerType = (response = {}) => {
  const data = response?.data || {};
  const nested = data.data || data.event || data.details || {};
  if (
    nested.reused_terminated_member === true ||
    nested.customer_type === "existing" ||
    data.customer_type === "existing"
  ) {
    return "existing";
  }
  return "new";
};

const buildEcommerceItems = (state = {}, selectedPlan = {}, billingCycle = "monthly") => {
  if (!selectedPlan) return [];

  const planCodes = parseMultiValueField(selectedPlan.plan_code);
  const planIds = parseMultiValueField(selectedPlan.plan_id);
  const monthlyParts = parseMultiValueField(
    selectedPlan.total_monthly_prem ?? selectedPlan.monthly_premium
  );
  const descriptions = (() => {
    const parsed = parseMultiValueField(selectedPlan.description);
    if (parsed.length > 1) return parsed;
    return splitPlanDescriptions(selectedPlan.description);
  })();
  const itemCategory = inferItemCategory(state, selectedPlan);
  const count = Math.max(planCodes.length, planIds.length, 1);

  if (count <= 1) {
    return [
      {
        item_id: planCodes[0] || String(planIds[0] || selectedPlan.plan_id || ""),
        item_name: selectedPlan.description || "",
        item_category: itemCategory,
        price: parseAnalyticsMoney(
          selectedPlan.total_monthly_prem ?? selectedPlan.monthly_premium
        ),
        quantity: 1,
        item_variant: billingCycle,
      },
    ];
  }

  return Array.from({ length: count }, (_, index) => ({
    item_id: planCodes[index] || String(planIds[index] || ""),
    item_name: descriptions[index] || descriptions[0] || selectedPlan.description || "",
    item_category: itemCategory,
    price: parseAnalyticsMoney(monthlyParts[index] ?? monthlyParts[0] ?? 0),
    quantity: 1,
    item_variant: billingCycle,
  })).filter((item) => item.item_id || item.item_name);
};

export const buildEnrollmentSuccessAnalytics = (
  state = {},
  {
    selectedPlan = null,
    memberDetails = {},
    response = {},
    serviceFee = "0.00",
    discount = "0.00",
  } = {}
) => {
  const billingCycle = resolveBillingCycle(state);
  const planPrice = parseAnalyticsMoney(
    selectedPlan?.total_monthly_prem ?? selectedPlan?.monthly_premium
  );
  const proratedPremium = parseAnalyticsMoney(
    selectedPlan?.total_prorated_prem ?? selectedPlan?.prorated_premium
  );
  const serviceFeeAmount = parseAnalyticsMoney(serviceFee);
  const discountAmount = parseAnalyticsMoney(
    discount || selectedPlan?.student_discount_amount || "0.00"
  );
  const items = buildEcommerceItems(state, selectedPlan, billingCycle);
  const transactionId = resolveTransactionId(response, state, memberDetails);
  const email = memberDetails.email || state.member?.email || "";
  const phone = memberDetails.phone || state.member?.phone || "";
  const memberState = memberDetails.state || state.member?.state || "";

  return {
    plan_name: selectedPlan?.description || "",
    ecommerce: {
      currency: "USD",
      value: planPrice,
      transaction_id: transactionId,
      items,
      plan_price: planPrice,
      prorated_premium: proratedPremium,
      service_fee: serviceFeeAmount,
      tax: 0,
      discount: discountAmount,
    },
    customer_email: email,
    phone_number: phone,
    customer_type: resolveCustomerType(response),
    billing_cycle: billingCycle,
    state: memberState,
    product_tier: inferProductTier(selectedPlan, state),
  };
};

export const pushEnrollmentSuccessEvent = (state = {}, context = {}) => {
  if (!isGeneralEnrollmentTrackingEnabled()) return;

  const successPayload = buildEnrollmentSuccessAnalytics(state, context);

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event: GENERAL_ENROLLMENT_EVENTS.SUCCESS,
    enrollment_flow: "general",
    environment: "test",
    ...buildGeneralEnrollmentContext(state, {
      plan_name: successPayload.plan_name,
    }),
    ecommerce: successPayload.ecommerce,
    customer_email: successPayload.customer_email,
    phone_number: successPayload.phone_number,
    customer_type: successPayload.customer_type,
    billing_cycle: successPayload.billing_cycle,
    state: successPayload.state,
    product_tier: successPayload.product_tier,
  });
};

export const pushGeneralEnrollmentEvent = (event, state = {}, extra = {}) => {
  if (!isGeneralEnrollmentTrackingEnabled()) return;

  window.dataLayer = window.dataLayer || [];
  window.dataLayer.push({
    event,
    enrollment_flow: "general",
    environment: "test",
    ...buildGeneralEnrollmentContext(state, extra),
  });
};
